import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import api from '../utils/api';
import { AuroraBlobs, GrainOverlay, GlassChip, MagneticButton, Reveal, fadeUp, stagger, Crumbs } from '../components/ui';
import { FiCheck, FiTruck, FiPackage } from 'react-icons/fi';

const STATUS_STEPS = ['Pending', 'Confirmed', 'Processing', 'Shipped', 'Delivered'];

export default function OrderTrackingPage() {
  const { id } = useParams();
  const { data, isLoading } = useQuery({
    queryKey: ['order-tracking', id],
    queryFn: () => api.get(`/orders/${id}`),
  });

  if (isLoading) return <div style={{ textAlign: 'center', padding: 120 }}>Loading…</div>;
  const order = data?.order;
  if (!order) return <div style={{ textAlign: 'center', padding: 120 }}>Order not found</div>;

  const currentStepIdx = STATUS_STEPS.indexOf(order.status);

  return (
    <div>
      {/* Hero */}
      <section style={{ position: 'relative', background: '#1A100E', overflow: 'hidden', padding: '52px 24px 44px' }}>
        <AuroraBlobs muted />
        <GrainOverlay />
        <div style={{ position: 'relative', zIndex: 2, maxWidth: 800, margin: '0 auto' }}>
          <Crumbs items={[{ label: 'Home', to: '/' }, { label: 'Orders', to: '/orders' }, { label: order.orderId }]} />
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <GlassChip>Order tracking</GlassChip>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1 }} style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 16, marginTop: 16, flexWrap: 'wrap' }}>
            <div>
              <h1 className="serif" style={{ fontSize: 'clamp(30px, 4vw, 44px)', color: '#FFF', lineHeight: 1.05, letterSpacing: '-0.02em' }}>{order.orderId}</h1>
              <div style={{ fontSize: 13, color: 'rgba(237,227,209,0.6)', marginTop: 6 }}>Placed on {new Date(order.createdAt).toLocaleDateString('en-IN', { year: 'numeric', month: 'long', day: 'numeric' })}</div>
            </div>
            <StatusPill status={order.status} big />
          </motion.div>
        </div>
      </section>

      <div style={{ maxWidth: 800, margin: '0 auto', padding: '36px 24px 64px', display: 'flex', flexDirection: 'column', gap: 18 }}>
        {/* Progress */}
        {!['Returned', 'Cancelled'].includes(order.status) && (
          <Reveal>
            <div className="ve-glass-form-card" style={{ padding: 32 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', position: 'relative' }}>
                <div style={{ position: 'absolute', top: 14, left: '6%', right: '6%', height: 3, background: 'var(--line-2)', borderRadius: 3 }}>
                  <motion.div initial={{ width: 0 }} animate={{ width: `${Math.min(100, (currentStepIdx / (STATUS_STEPS.length - 1)) * 100)}%` }} transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
                    style={{ height: '100%', background: 'linear-gradient(90deg, var(--primary), var(--gold))', borderRadius: 3 }} />
                </div>
                {STATUS_STEPS.map((step, i) => {
                  const done = i < currentStepIdx;
                  const active = i === currentStepIdx;
                  return (
                    <motion.div key={step} initial={{ opacity: 0, scale: 0.5 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5, delay: 0.2 + i * 0.1 }}
                      style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, flex: 1, zIndex: 1 }}>
                      <motion.div animate={active ? { boxShadow: ['0 0 0 0 rgba(176,138,62,0.5)', '0 0 0 10px rgba(176,138,62,0)'] } : {}} transition={{ duration: 1.6, repeat: active ? Infinity : 0 }}
                        style={{
                          width: 30, height: 30, borderRadius: '50%',
                          background: done ? 'var(--primary)' : active ? 'var(--gold)' : '#FFF',
                          border: `2px solid ${done ? 'var(--primary)' : active ? 'var(--gold)' : 'var(--line)'}`,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                        {done && <FiCheck size={14} color="#FFF" />}
                      </motion.div>
                      <div style={{ fontSize: 11, fontWeight: active || done ? 700 : 500, color: active ? 'var(--gold)' : done ? 'var(--primary)' : 'var(--muted)', textAlign: 'center', letterSpacing: '0.08em', textTransform: 'uppercase' }}>{step}</div>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          </Reveal>
        )}

        {/* Shipping info */}
        {order.shipping?.awb && (
          <Reveal>
            <div className="ve-glass-form-card" style={{ padding: 24 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                <span style={{ width: 36, height: 36, borderRadius: 10, background: 'var(--gold-soft)', color: 'var(--gold)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <FiTruck size={16} />
                </span>
                <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase', color: 'var(--muted)' }}>Shipping Details</div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16 }}>
                <KV k="Courier" v={order.shipping.courier || 'Delhivery'} />
                <KV k="AWB" v={order.shipping.awb} />
                {order.shipping.estimatedDelivery && <KV k="Expected" v={new Date(order.shipping.estimatedDelivery).toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' })} />}
              </div>
            </div>
          </Reveal>
        )}

        {/* Timeline */}
        <Reveal>
          <div className="ve-glass-form-card" style={{ padding: 24 }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase', color: 'var(--gold)', marginBottom: 18 }}>Order Timeline</div>
            <motion.div variants={stagger} initial="hidden" animate="show">
              {[...order.timeline].reverse().map((t, i) => (
                <motion.div key={i} variants={fadeUp} style={{ display: 'flex', gap: 16, paddingBottom: i < order.timeline.length - 1 ? 18 : 0 }}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <div style={{ width: 12, height: 12, borderRadius: '50%', background: i === 0 ? 'var(--primary)' : 'var(--line)', marginTop: 4, boxShadow: i === 0 ? '0 0 0 4px rgba(122,31,43,0.12)' : 'none' }} />
                    {i < order.timeline.length - 1 && <div style={{ width: 2, flex: 1, background: 'var(--line-2)', marginTop: 6, borderRadius: 1 }} />}
                  </div>
                  <div style={{ flex: 1, paddingBottom: 12 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: i === 0 ? 'var(--ink)' : 'var(--ink-2)' }}>{t.status}</div>
                    <div style={{ fontSize: 12, color: 'var(--ink-2)', marginTop: 2 }}>{t.message}</div>
                    <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 4, letterSpacing: '0.06em' }}>{new Date(t.timestamp).toLocaleString('en-IN')}</div>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </Reveal>

        {/* Items */}
        <Reveal>
          <div className="ve-glass-form-card" style={{ padding: 24 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
              <FiPackage size={14} color="var(--gold)" />
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase', color: 'var(--gold)' }}>Items ({order.items.length})</div>
            </div>
            {order.items.map((item, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderTop: i > 0 ? '1px dashed var(--line-2)' : 'none' }}>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 600 }}>{item.name}</div>
                  <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 3 }}>Size {item.size} · Qty {item.qty}</div>
                </div>
                <div className="serif" style={{ fontSize: 16, fontWeight: 600 }}>₹{(item.price * item.qty).toLocaleString('en-IN')}</div>
              </div>
            ))}
            <div style={{ borderTop: '1px solid var(--line)', marginTop: 14, paddingTop: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
              <span style={{ fontSize: 14, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase' }}>Total</span>
              <span className="serif" style={{ fontSize: 26, fontWeight: 600 }}>₹{order.pricing.total?.toLocaleString('en-IN')}</span>
            </div>
          </div>
        </Reveal>

        <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
          <MagneticButton to="/orders" variant="ghost">View all orders</MagneticButton>
          <a href={`https://wa.me/${process.env.REACT_APP_WHATSAPP_NUMBER || '919999999999'}?text=${encodeURIComponent(`Help with order ${order.orderId}`)}`} target="_blank" rel="noreferrer" style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            padding: '0 22px', height: 44, borderRadius: 999,
            background: 'rgba(37,211,102,0.1)', border: '1px solid rgba(37,211,102,0.3)',
            color: '#25D366', textDecoration: 'none',
            fontSize: 12, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase',
          }}>
            WhatsApp Support ↗
          </a>
        </div>
      </div>
    </div>
  );
}

function StatusPill({ status, big }) {
  const map = {
    Delivered: { bg: '#E5EFE0', color: '#3F6240' }, Shipped: { bg: '#DDE7F0', color: '#34557A' },
    Confirmed: { bg: '#DDE7F0', color: '#34557A' }, Pending: { bg: '#FBEED6', color: '#8A6020' },
    Returned: { bg: '#F1DBD8', color: '#8B3F36' }, Cancelled: { bg: '#F1DBD8', color: '#8B3F36' },
  };
  const style = map[status] || map.Pending;
  return (
    <span style={{
      ...style, padding: big ? '8px 16px' : '5px 12px', borderRadius: 999,
      fontSize: big ? 12 : 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase',
    }}>
      {status}
    </span>
  );
}

function KV({ k, v }) {
  return (
    <div>
      <div style={{ fontSize: 10, color: 'var(--muted)', letterSpacing: '0.14em', textTransform: 'uppercase', fontWeight: 700, marginBottom: 4 }}>{k}</div>
      <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink)' }}>{v}</div>
    </div>
  );
}
